import { useCallback } from "react";

import { makeStyles } from "@material-ui/core";

import { LightAndGrayButton } from "../components/button";
import { useUser } from "../user/UserProvider";
import { useCompanyByCvr } from "../company/company.hook";
import { usePersonalInformation } from "./personal-information.hook";
import { UpdateGardenerFormIds, useUpdateGardenerDialog } from "./UpdateGardenerDialog";
import { TaskResponseData, useTaskResponseAction } from "../task/task-response-action.hook";
import { FormProps } from "../components/form-item/framework/react/FormProvider";
import { FormItem } from "../components/form-item/framework/core/form-item.model";
import { AddressPickerFormItem } from "../components/form-item/client/form-item/AddressPickerFormItemComponent";
import { SingleChoiceFormItem, TextAreaFormItem } from "../components/form-item/client/components";
import { UpdateGardenerReasonsPageFormItem } from "../components/form-item/client/form-item/UpdateGardenerReasonsPage";
import { WorkingAs } from "../company/utility";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",

        padding: "24px",

        "& > *:first-child": {
            marginRight: "48px",
        },

        [theme.breakpoints.down(1200)]: {
            flexDirection: "column",
            textAlign: "center",

            "& > *:first-child": {
                marginRight: 0,
                marginBottom: "16px",
            },

            "& > *:first-child:last-child": {
                marginBottom: 0,
            },
        },
    },
    notice: {
        backgroundColor: "#A259FF",
    },
    title: {
        fontSize: "16px",
        fontWeight: 600,
        color: "#FFFFFF",
        margin: 0,
        marginBottom: "4px",
    },
    message: {
        fontSize: "16px",
        fontWeight: 400,
        color: "#FFFFFF",
        margin: 0,
    },
}));

export const CompleteProfileBanner = () => {
    const classes = useStyles();

    const gardener = useUser();
    const company = useCompanyByCvr(gardener.cvr);
    const personalInformation = usePersonalInformation(gardener.id);

    const taskResponseAction = useTaskResponseAction();

    const updateGardenerResponseAdapter = useCallback<FormProps<FormItem>["onSubmit"]>((controller, form): void => {
        const selectedChoiceIndex = (controller.getItem(UpdateGardenerFormIds.SingleChoiceWorkingAs, form) as SingleChoiceFormItem<any>).selectedChoiceIndex!;
        const answerTexts = (controller.getItem(UpdateGardenerFormIds.SingleChoiceWorkingAs, form) as SingleChoiceFormItem<any>).answerTexts;
        const workingAs = answerTexts[selectedChoiceIndex] as WorkingAs;

        if ( workingAs === "Privatperson" ) {
            const reasonsPage = (controller.getItem(UpdateGardenerFormIds.PageReasons, form) as UpdateGardenerReasonsPageFormItem);
            const name = (controller.getItem(UpdateGardenerFormIds.TextAreaName, form) as TextAreaFormItem).value.trim();
            const nameChangeReason = (controller.getItem(UpdateGardenerFormIds.TextAreaNameChangeReason, form) as TextAreaFormItem).value.trim();
            const externalAddressId = (controller.getItem(UpdateGardenerFormIds.AddressPickerAddress, form) as AddressPickerFormItem).value!.externalId;
            const cpr = (controller.getItem(UpdateGardenerFormIds.TextAreaCpr, form) as TextAreaFormItem).value;
            const cprChangeReason = (controller.getItem(UpdateGardenerFormIds.TextAreaCprChangeReason, form) as TextAreaFormItem).value.trim();

            const data: TaskResponseData = {
                action: "update individual seller",
                name,
                nameChangeReason: name.toLowerCase() !== reasonsPage.initialName.toLowerCase() ? nameChangeReason : null,
                externalAddressId,
                cpr,
                cprChangeReason: reasonsPage.initialCpr && cpr !== reasonsPage.initialCpr ? cprChangeReason : null,
                cvr: null,
            };
            return taskResponseAction(data);
        }

        if ( workingAs === "Personligt ejet mindre virksomhed" || workingAs === "Enkeltmandsvirksomhed" ) {
            const reasonsPage = (controller.getItem(UpdateGardenerFormIds.PageReasons, form) as UpdateGardenerReasonsPageFormItem);
            const name = (controller.getItem(UpdateGardenerFormIds.TextAreaName, form) as TextAreaFormItem).value.trim();
            const nameChangeReason = (controller.getItem(UpdateGardenerFormIds.TextAreaNameChangeReason, form) as TextAreaFormItem).value.trim();
            const externalAddressId = (controller.getItem(UpdateGardenerFormIds.AddressPickerAddress, form) as AddressPickerFormItem).value!.externalId;
            const cpr = (controller.getItem(UpdateGardenerFormIds.TextAreaCpr, form) as TextAreaFormItem).value;
            const cprChangeReason = (controller.getItem(UpdateGardenerFormIds.TextAreaCprChangeReason, form) as TextAreaFormItem).value.trim();
            const cvr = (controller.getItem(UpdateGardenerFormIds.TextAreaCvr, form) as TextAreaFormItem).value || null;

            const data: TaskResponseData = {
                action: "update individual seller",
                name,
                nameChangeReason: name.toLowerCase() !== reasonsPage.initialName.toLowerCase() ? nameChangeReason : null,
                externalAddressId,
                cpr,
                cprChangeReason: reasonsPage.initialCpr && cpr !== reasonsPage.initialCpr ? cprChangeReason : null,
                cvr,
            };
            return taskResponseAction(data);
        }

        if ( workingAs === "Anden juridisk enhed" ) {
            const externalAddressId = (controller.getItem(UpdateGardenerFormIds.AddressPickerAddress, form) as AddressPickerFormItem).value!.externalId;
            const data: TaskResponseData = {
                action: "update entity seller",
                externalAddressId,
            };
            return taskResponseAction(data);
        }
    }, [taskResponseAction]);

    const openUpdateGardenerDialog = useUpdateGardenerDialog(gardener, company?.data ?? null, personalInformation ?? null, updateGardenerResponseAdapter);

    return (
        <div className={`${classes.root} ${classes.notice}`}>

            <div>
                <h4 className={classes.title}>Udfyld dine stamdata</h4>
                <p className={classes.message}>
                    Du kan tilføje dit CPR-nummer nu, eller når du accepterer din første opgave. DAC7-lovgivningen kræver, at vi indsamler det.
                </p>
            </div>

            <div>
                <LightAndGrayButton onClick={openUpdateGardenerDialog}>Udfyld stamdata</LightAndGrayButton>
            </div>

        </div>
    );
};
